import { useMemo } from "react";
import { HomeGainDataItem } from "@api/useHomeGainData";
import { useDisplayMetricContext, useHomeContext } from "@pages/Home/HomeContext";
import { useKeywordNameTableCell } from "@vms/TableCells/useKeywordNameTableCell";
import { useMacStageTableCell } from "@vms/TableCells/useMacStageTableCell";
import { getRem } from "@datassential/platform-ui-lib";
import { useGetMetric } from "@core/CommonDataContext";
import { AffinityAmong, cleanUpMetricName, HOME_GAIN_AFFINITY_ORDER, Metric } from "@models/Metric";
import { TableColumn } from "@components/Table/TableColumns";
import { useBaseSizeTableCell } from "@vms/TableCells/useBaseSizeTableCell";

export type GainViewTableConfigProps = {
    data: HomeGainDataItem[];
}

const COLUMN_WIDTH = 120;

const AffinityAmongToMetric = {
    [AffinityAmong.All]: null,
    [AffinityAmong.KnowIt]: Metric.KnowIt,
    [AffinityAmong.HaveTriedIt]: Metric.HaveTriedIt,
}

export function useGainViewTableConfig({
    data,
}: GainViewTableConfigProps) {
    const { search } = useHomeContext();
    const trimmedSearch = useMemo(() => search.trim(), [search]);
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch]);

    const { displayMetricId } = useDisplayMetricContext();

    const nameColumn = useKeywordNameTableCell(data, trimmedSearch);
    const macStageColumn = useMacStageTableCell(data);
    const baseSizeColumn = useBaseSizeTableCell(data);
    const getMetric = useGetMetric();

    const displayMetric = useMemo(() => getMetric(displayMetricId), [displayMetricId, getMetric]);

    return useMemo(
        () => [
            {
                ...nameColumn,
                sortKey: hideSorting ? undefined : 'name',
                style: {
                    ...nameColumn.style,
                    cell: {
                        ...nameColumn.style?.cell,
                        flex: `1 0 ${getRem(190)}`,
                    }
                },
            },
            {
                ...macStageColumn,
                sortKey: hideSorting ? undefined : 'macStage',
            },
            {
                ...baseSizeColumn,
                sortKey: hideSorting ? undefined : 'base',
            },
            {
                key: `metric-${displayMetricId}`,
                head: (
                    <span
                        className="HomeGainView__table-main-text">{cleanUpMetricName(displayMetric?.name ?? "All", true)}
                    </span>
                ),
                Value: () => null,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(5 * COLUMN_WIDTH)}`,
                        justifyContent: 'center',
                    },
                },
                subColumns: HOME_GAIN_AFFINITY_ORDER.map((amongId, amongIndex) => {
                    const isAllConsumers = amongId === AffinityAmong.All;
                    const metricId = AffinityAmongToMetric[amongId];
                    const metric = metricId !== null ? getMetric(metricId) : null;
                    const head = metric ? `Those Who ${metric?.name}` : 'All Consumers';

                    const percentKey = `${amongId}`;
                    const changeKey = `${amongId}Change`;

                    const percentColumn: TableColumn = {
                        key: `percent-${amongId}`,
                        head: '%',
                        headText: `${head} %`,
                        Value: ({ index }) => <>
                            {data[index].columns.get(percentKey)?.tableValue ?? ''}
                        </>,
                        valueText: (index) => data[index].columns.get(percentKey)?.copyTableValue ?? '',
                        sortKey: hideSorting ? undefined : `column${amongId}`,
                        style: {
                            cell: {
                                flex: `0 0 ${getRem(COLUMN_WIDTH)}`,
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 1 + (amongIndex) % 2,
                    };
                    const changeColumn: TableColumn = {
                        key: `percent-${amongId}-change`,
                        head: 'chg',
                        headText: `${head} chg`,
                        Value: ({ index }) => <>
                            {data[index].columns.get(changeKey)?.tableValue ?? ''}
                        </>,
                        valueText: (index) => data[index].columns.get(changeKey)?.copyTableValue ?? '',
                        sortKey: hideSorting ? undefined : `column${amongId}Change`,
                        style: {
                            cell: {
                                flex: `0 0 ${getRem(COLUMN_WIDTH)}`,
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 1 + (amongIndex) % 2,
                    };

                    const subColumns = [percentColumn];
                    if (!isAllConsumers) {
                        subColumns.push(changeColumn);
                    }

                    return {
                        key: `those-${amongId}`,
                        head: head,
                        Value: () => null,
                        style: {
                            cell: {
                                flex: `0 0 ${getRem(subColumns.length * COLUMN_WIDTH)}`,
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 2 + (amongIndex) % 2,
                        subColumns,
                    };
                }),
            }
        ],
        [
            nameColumn,
            macStageColumn,
            hideSorting,
            baseSizeColumn,
            data,
            displayMetricId,
            displayMetric?.name,
            getMetric,
        ]
    );
}